// Theme Lab — premium side-by-side design with live preview.
// FIXES the flash bug from v2 (where close() setTimeout removed the new modal).

import { theme } from "./prefs.js";
import { formActions } from "./form_actions.js";
import { uxPrefs } from "./ux_prefs.js";
import { t, accentName } from "../utils/i18n.js";
import { svg } from "../utils/icons.js";
import { userKey } from "../utils/storage.js";

const ROOT_ID = "e16-picker-root";
// Per-user key so a custom accent hex is isolated between users on a
// shared browser (Theme Lab user-scoping).
const CUSTOM_HEX_KEY = () => userKey("custom_hex");
const ACCENTS = ["blue", "green", "red", "orange", "yellow", "pink", "violet"];

const ACCENT_HEX = {
	blue:   "#2563EB",
	green:  "#10B981",
	red:    "#EF4444",
	orange: "#F97316",
	yellow: "#F59E0B",
	pink:   "#EC4899",
	violet: "#7C3AED",
};

// === Close (capture element ref so setTimeout doesn't remove the WRONG modal) ===
function close() {
	const el = document.getElementById(ROOT_ID);
	if (!el) return;
	el.classList.add("is-out");
	// Capture reference — DON'T re-query inside setTimeout (bug fix)
	setTimeout(() => {
		try { if (el && el.parentNode) el.parentNode.removeChild(el); } catch (e) {}
	}, 180);
}

// === Custom hex color helpers ===
function hexToRgb(hex) {
	let h = hex.replace("#", "");
	if (h.length === 3) h = h.split("").map((c) => c + c).join("");
	const num = parseInt(h, 16);
	return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex(r, g, b) {
	const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
	return "#" + to(r) + to(g) + to(b);
}

function darken(hex, p) {
	const { r, g, b } = hexToRgb(hex);
	const f = 1 - p / 100;
	return rgbToHex(r * f, g * f, b * f);
}

function lighten(hex, p) {
	const { r, g, b } = hexToRgb(hex);
	const f = p / 100;
	return rgbToHex(r + (255 - r) * f, g + (255 - g) * f, b + (255 - b) * f);
}

function applyCustomHex(hex) {
	if (!hex || !/^#[0-9a-fA-F]{3,8}$/.test(hex)) return false;
	const root = document.documentElement;
	const hover = darken(hex, 10);
	const light = lighten(hex, 18);
	root.style.setProperty("--e16-accent", hex, "important");
	root.style.setProperty("--e16-accent-hover", hover, "important");
	root.style.setProperty("--e16-accent-light", light, "important");
	root.style.setProperty("--primary", hex, "important");
	root.style.setProperty("--primary-color", hex, "important");
	root.style.setProperty("--btn-primary", hex, "important");
	return true;
}

function resetCustomHex() {
	const root = document.documentElement;
	root.style.removeProperty("--e16-accent");
	root.style.removeProperty("--e16-accent-hover");
	root.style.removeProperty("--e16-accent-light");
	root.style.removeProperty("--primary");
	root.style.removeProperty("--primary-color");
	root.style.removeProperty("--btn-primary");
}

function buildModal() {
	// IDEMPOTENT: if modal exists, do nothing
	if (document.getElementById(ROOT_ID)) return;

	const lng = theme.currentLang();
	const cur = {
		accent: theme.currentAccent(),
		mode: theme.currentMode(),
		lang: theme.currentLang(),
		...theme.currentPaint(),
	};
	const initial = { ...cur };
	const initialHex = localStorage.getItem(CUSTOM_HEX_KEY()) || "";
	const fa = formActions.getPrefs();
	const ux = uxPrefs.get();

	const overlay = document.createElement("div");
	overlay.id = ROOT_ID;
	overlay.className = "e16-lab-overlay";
	overlay.setAttribute("role", "dialog");
	overlay.setAttribute("aria-modal", "true");

	overlay.innerHTML = `
		<div class="e16-lab">
			<!-- HEADER -->
			<div class="e16-lab__head">
				<div class="e16-lab__brand">
					<div class="e16-lab__icon">${svg("palette", { size: 22 })}</div>
					<div>
						<div class="e16-lab__title">${lng === "ar" ? "مختبر الثيم" : "Theme Lab"}</div>
						<div class="e16-lab__sub">${lng === "ar" ? "صمّم هويتك البصرية" : "Craft your signature look"}</div>
					</div>
				</div>
				<button type="button" class="e16-lab__close" aria-label="${t("Close", lng)}">
					${svg("x", { size: 18 })}
				</button>
			</div>

			<!-- BODY: split left preview / right controls -->
			<div class="e16-lab__body">

				<!-- LEFT: LIVE PREVIEW -->
				<div class="e16-lab__preview">
					<div class="e16-lab__preview-label">${lng === "ar" ? "معاينة حيّة" : "Live preview"}</div>

					<!-- Mini app preview -->
					<div class="e16-mini">
						<div class="e16-mini__sidebar">
							<div class="e16-mini__brand"></div>
							<div class="e16-mini__nav active"></div>
							<div class="e16-mini__nav"></div>
							<div class="e16-mini__nav"></div>
							<div class="e16-mini__nav"></div>
						</div>
						<div class="e16-mini__content">
							<div class="e16-mini__header">
								<div class="e16-mini__btn-primary"></div>
								<div class="e16-mini__btn-secondary"></div>
							</div>
							<div class="e16-mini__row">
								<div class="e16-mini__card">
									<div class="e16-mini__card-icon"></div>
									<div class="e16-mini__bars">
										<div class="e16-mini__bar"></div>
										<div class="e16-mini__bar short"></div>
									</div>
								</div>
								<div class="e16-mini__card alt">
									<div class="e16-mini__card-icon alt"></div>
									<div class="e16-mini__bars">
										<div class="e16-mini__bar"></div>
										<div class="e16-mini__bar short"></div>
									</div>
								</div>
							</div>
							<div class="e16-mini__chart"></div>
						</div>
					</div>

					<div class="e16-lab__preview-strip">
						<span class="e16-lab__pill">${lng === "ar" ? "نشط" : "Active"}</span>
						<span class="e16-lab__chip">${lng === "ar" ? "وسم" : "Tag"}</span>
						<span class="e16-lab__link">${lng === "ar" ? "رابط" : "Link"}</span>
					</div>
				</div>

				<!-- RIGHT: CONTROLS -->
				<div class="e16-lab__controls">

					<!-- Preset colors -->
					<div class="e16-lab-section">
						<div class="e16-lab-section__title">
							<span class="e16-lab-section__dot"></span>
							${lng === "ar" ? "الألوان الجاهزة" : "Preset Colors"}
						</div>
						<div class="e16-lab__colors" role="radiogroup">
							${ACCENTS.map((a) => `
								<button type="button"
									class="e16-color-dot ${a === cur.accent ? "is-active" : ""}"
									data-color="${a}"
									role="radio"
									aria-checked="${a === cur.accent}"
									aria-label="${accentName(a, lng)}"
									title="${accentName(a, lng)}">
									<svg class="e16-color-dot__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="20 6 9 17 4 12"/>
									</svg>
								</button>
							`).join("")}
						</div>
					</div>

					<!-- Custom color -->
					<div class="e16-lab-section">
						<div class="e16-lab-section__title">
							<span class="e16-lab-section__dot"></span>
							${lng === "ar" ? "لون مخصّص" : "Custom Color"}
						</div>
						<div class="e16-lab-custom">
							<label class="e16-lab-custom__swatch-wrap" title="${lng === "ar" ? "اختر لوناً" : "Pick a color"}">
								<input type="color" class="e16-lab-custom__swatch" value="${initialHex || ACCENT_HEX[cur.accent]}">
								<div class="e16-lab-custom__swatch-overlay">${svg("palette", { size: 14 })}</div>
							</label>
							<input type="text"
								class="e16-lab-custom__hex"
								value="${initialHex || ACCENT_HEX[cur.accent]}"
								maxlength="9"
								placeholder="#RRGGBB"
								spellcheck="false">
							<button type="button" class="e16-lab-custom__apply">
								${lng === "ar" ? "تطبيق" : "Apply"}
							</button>
						</div>
					</div>

					<!-- Apply on -->
					<div class="e16-lab-section">
						<div class="e16-lab-section__title">
							<span class="e16-lab-section__dot"></span>
							${lng === "ar" ? "تطبيق على" : "Apply On"}
						</div>
						<div class="e16-lab-checks">
							<label class="e16-check">
								<input type="checkbox" data-paint="paint_menu" ${cur.paint_menu ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${t("Apply on Menu", lng)}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-paint="paint_dashboard" ${cur.paint_dashboard ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${t("Apply on Dashboard", lng)}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-paint="paint_workspace" ${cur.paint_workspace ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${t("Apply on Workspace", lng)}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-paint="paint_navbar" ${cur.paint_navbar ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${t("Apply on Navbar", lng)}</span>
							</label>
						</div>
					</div>

					<!-- Smart Actions -->
					<div class="e16-lab-section">
						<div class="e16-lab-section__title">
							<span class="e16-lab-section__dot"></span>
							${lng === "ar" ? "إجراءات ذكية" : "Smart Actions"}
						</div>
						<div class="e16-lab-checks">
							<label class="e16-check">
								<input type="checkbox" data-fa="save_submit" ${fa.save_submit ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "زر «حفظ و ترحيل»" : "“Save & Submit” button"}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-fa="cancel_delete" ${fa.cancel_delete ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "زر «إلغاء و حذف»" : "“Cancel & Delete” button"}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-fa="cancel_edit" ${fa.cancel_edit ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "زر «إلغاء و تعديل»" : "“Cancel & Edit” button"}</span>
							</label>
						</div>
					</div>

					<!-- UX Power -->
					<div class="e16-lab-section">
						<div class="e16-lab-section__title">
							<span class="e16-lab-section__dot"></span>
							${lng === "ar" ? "تجربة المستخدم القوية" : "Power UX"}
						</div>
						<div class="e16-lab-section__title-sm">${lng === "ar" ? "الكثافة" : "Density"}</div>
						<div class="e16-seg" role="radiogroup">
							<button type="button" class="e16-seg__btn ${ux.density === "compact" ? "is-active" : ""}" data-ux-density="compact">${lng === "ar" ? "مدمج" : "Compact"}</button>
							<button type="button" class="e16-seg__btn ${ux.density === "comfortable" ? "is-active" : ""}" data-ux-density="comfortable">${lng === "ar" ? "مريح" : "Comfortable"}</button>
							<button type="button" class="e16-seg__btn ${ux.density === "wide" ? "is-active" : ""}" data-ux-density="wide">${lng === "ar" ? "واسع" : "Wide"}</button>
						</div>
						<div class="e16-lab-checks" style="margin-top:10px">
							<label class="e16-check">
								<input type="checkbox" data-ux="focus" ${ux.focus ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "وضع التركيز (إخفاء الزخارف)" : "Focus mode (hide decoration)"}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-ux="accountant" ${ux.accountant ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "أدوات المحاسب (المبلغ كتابةً + نسخ سريع)" : "Accountant tools (amount-in-words + quick copy)"}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-ux="keyboard" ${ux.keyboard ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "اختصارات لوحة المفاتيح (g ثم l/h/n/r/f)" : "Keyboard shortcuts (g then l/h/n/r/f)"}</span>
							</label>
							<label class="e16-check">
								<input type="checkbox" data-ux="pdf_export" ${ux.pdf_export ? "checked" : ""}>
								<span class="e16-check__box">${svg("check", { size: 11 })}</span>
								<span class="e16-check__label">${lng === "ar" ? "زر 📄 تصدير PDF على المستندات المحفوظة" : "📄 Export PDF button on saved documents"}</span>
							</label>
						</div>
					</div>

					<!-- Mode + Language in same row -->
					<div class="e16-lab-section e16-lab-section--row">
						<div class="e16-lab-section__half">
							<div class="e16-lab-section__title-sm">${t("Mode", lng)}</div>
							<div class="e16-seg" role="radiogroup">
								<button type="button" class="e16-seg__btn ${cur.mode === "light" ? "is-active" : ""}" data-mode="light">
									${svg("sun", { size: 13 })} <span>${t("Light", lng)}</span>
								</button>
								<button type="button" class="e16-seg__btn ${cur.mode === "dark" ? "is-active" : ""}" data-mode="dark">
									${svg("moon", { size: 13 })} <span>${t("Dark", lng)}</span>
								</button>
							</div>
						</div>
						<div class="e16-lab-section__half">
							<div class="e16-lab-section__title-sm">${t("Language", lng)}</div>
							<div class="e16-seg" role="radiogroup">
								<button type="button" class="e16-seg__btn ${cur.lang === "en" ? "is-active" : ""}" data-lang="en">EN</button>
								<button type="button" class="e16-seg__btn ${cur.lang === "ar" ? "is-active" : ""}" data-lang="ar">AR</button>
							</div>
						</div>
					</div>

				</div>

			</div>

			<!-- FOOTER -->
			<div class="e16-lab__foot">
				<button type="button" class="e16-btn e16-btn--ghost" data-action="cancel">${t("Cancel", lng)}</button>
				<button type="button" class="e16-btn e16-btn--primary" data-action="save">
					${svg("check", { size: 14 })} ${t("Save Settings", lng)}
				</button>
			</div>
		</div>
	`;

	document.body.appendChild(overlay);

	const draft = { ...cur, custom_hex: initialHex || null };

	// ===== Wire interactions =====

	overlay.querySelectorAll(".e16-color-dot").forEach((dot) => {
		dot.addEventListener("click", (ev) => {
			ev.stopPropagation();
			const a = dot.dataset.color;
			overlay.querySelectorAll(".e16-color-dot").forEach((d) => {
				d.classList.toggle("is-active", d === dot);
				d.setAttribute("aria-checked", d === dot ? "true" : "false");
			});
			draft.accent = a;
			draft.custom_hex = null;
			resetCustomHex();
			theme.applyAccent(a, { persist: false });
			const hex = ACCENT_HEX[a];
			overlay.querySelector(".e16-lab-custom__swatch").value = hex;
			overlay.querySelector(".e16-lab-custom__hex").value = hex;
		});
	});

	const swatch = overlay.querySelector(".e16-lab-custom__swatch");
	const hexInput = overlay.querySelector(".e16-lab-custom__hex");
	const customBtn = overlay.querySelector(".e16-lab-custom__apply");

	swatch.addEventListener("input", (ev) => {
		ev.stopPropagation();
		const hex = swatch.value;
		hexInput.value = hex.toUpperCase();
		applyCustomHex(hex);
		draft.custom_hex = hex;
		overlay.querySelectorAll(".e16-color-dot").forEach((d) => {
			d.classList.remove("is-active");
			d.setAttribute("aria-checked", "false");
		});
	});

	hexInput.addEventListener("input", (ev) => {
		ev.stopPropagation();
		const v = hexInput.value.trim();
		if (/^#[0-9a-fA-F]{3,8}$/.test(v)) {
			swatch.value = v.length === 4 ? "#" + v.slice(1).split("").map((c) => c + c).join("") : v.slice(0, 7);
			applyCustomHex(v);
			draft.custom_hex = v;
			overlay.querySelectorAll(".e16-color-dot").forEach((d) => {
				d.classList.remove("is-active");
				d.setAttribute("aria-checked", "false");
			});
		}
	});

	customBtn.addEventListener("click", (ev) => {
		ev.stopPropagation();
		const hex = hexInput.value.trim();
		if (applyCustomHex(hex)) draft.custom_hex = hex;
	});

	overlay.querySelectorAll('input[type="checkbox"][data-paint]').forEach((cb) => {
		cb.addEventListener("change", (ev) => {
			ev.stopPropagation();
			draft[cb.dataset.paint] = cb.checked ? 1 : 0;
			theme.applyPaint({
				paint_menu: draft.paint_menu,
				paint_dashboard: draft.paint_dashboard,
				paint_workspace: draft.paint_workspace,
				paint_navbar: draft.paint_navbar,
			}, { persist: false });
		});
	});

	overlay.querySelectorAll('input[type="checkbox"][data-fa]').forEach((cb) => {
		cb.addEventListener("change", (ev) => {
			ev.stopPropagation();
			fa[cb.dataset.fa] = cb.checked ? 1 : 0;
			formActions.setPrefs({
				save_submit: fa.save_submit,
				cancel_delete: fa.cancel_delete,
				cancel_edit: fa.cancel_edit,
			});
		});
	});

	overlay.querySelectorAll('.e16-seg__btn[data-ux-density]').forEach((btn) => {
		btn.addEventListener("click", (ev) => {
			ev.stopPropagation();
			const d = btn.dataset.uxDensity;
			overlay.querySelectorAll('.e16-seg__btn[data-ux-density]')
				.forEach((b) => b.classList.toggle("is-active", b === btn));
			uxPrefs.set({ density: d });
		});
	});

	overlay.querySelectorAll('input[type="checkbox"][data-ux]').forEach((cb) => {
		cb.addEventListener("change", (ev) => {
			ev.stopPropagation();
			uxPrefs.set({ [cb.dataset.ux]: cb.checked ? 1 : 0 });
		});
	});

	overlay.querySelectorAll('.e16-seg__btn[data-mode]').forEach((btn) => {
		btn.addEventListener("click", (ev) => {
			ev.stopPropagation();
			const m = btn.dataset.mode;
			overlay.querySelectorAll(".e16-seg__btn[data-mode]").forEach((b) => b.classList.toggle("is-active", b === btn));
			draft.mode = m;
			theme.applyMode(m, { persist: false });
		});
	});

	overlay.querySelectorAll('.e16-seg__btn[data-lang]').forEach((btn) => {
		btn.addEventListener("click", (ev) => {
			ev.stopPropagation();
			const l = btn.dataset.lang;
			overlay.querySelectorAll(".e16-seg__btn[data-lang]").forEach((b) => b.classList.toggle("is-active", b === btn));
			draft.lang = l;
		});
	});

	const revertAll = () => {
		theme.applyAccent(initial.accent, { persist: false });
		theme.applyMode(initial.mode, { persist: false });
		theme.applyPaint({
			paint_menu: initial.paint_menu,
			paint_dashboard: initial.paint_dashboard,
			paint_workspace: initial.paint_workspace,
			paint_navbar: initial.paint_navbar,
		}, { persist: false });
		// If initial had a custom hex, re-apply it; else clear
		if (initialHex) applyCustomHex(initialHex);
		else resetCustomHex();
	};

	overlay.querySelector(".e16-lab__close").addEventListener("click", (ev) => {
		ev.stopPropagation();
		revertAll();
		close();
	});

	overlay.querySelector('[data-action="cancel"]').addEventListener("click", (ev) => {
		ev.stopPropagation();
		revertAll();
		close();
	});

	overlay.querySelector('[data-action="save"]').addEventListener("click", (ev) => {
		ev.stopPropagation();
		const langChanged = draft.lang !== initial.lang;

		const args = {
			accent: draft.accent,
			mode: draft.mode,
			lang: draft.lang,
			paint_menu: draft.paint_menu,
			paint_dashboard: draft.paint_dashboard,
			paint_workspace: draft.paint_workspace,
			paint_navbar: draft.paint_navbar,
		};

		if (draft.custom_hex) {
			try { localStorage.setItem(CUSTOM_HEX_KEY(), draft.custom_hex); } catch (e) {}
		} else {
			try { localStorage.removeItem(CUSTOM_HEX_KEY()); } catch (e) {}
		}

		if (window.frappe && frappe.session && frappe.session.user !== "Guest") {
			frappe.call({
				method: "erpnext16_theme.api.preferences.set_prefs",
				args,
				freeze: false,
			}).then(() => {
				if (window.frappe && frappe.show_alert) {
					frappe.show_alert({ message: t("Saved", theme.currentLang()), indicator: "green" });
				}
				if (langChanged) {
					theme._reloadCacheBust();
				} else {
					close();
				}
			}).catch(() => close());
		} else {
			close();
		}
	});

	// Click on backdrop closes (but stopPropagation on inner panel prevents bubbling)
	overlay.querySelector(".e16-lab").addEventListener("click", (ev) => ev.stopPropagation());
	overlay.addEventListener("click", (ev) => {
		if (ev.target === overlay) {
			revertAll();
			close();
		}
	});

	// Esc closes
	const onKey = (ev) => {
		if (ev.key === "Escape") {
			ev.preventDefault();
			revertAll();
			close();
			document.removeEventListener("keydown", onKey);
		}
	};
	document.addEventListener("keydown", onKey);

	// Apply initial custom hex if one is stored
	if (initialHex) applyCustomHex(initialHex);
}

function applyStoredCustomHex() {
	try {
		const hex = localStorage.getItem(CUSTOM_HEX_KEY());
		if (hex) applyCustomHex(hex);
	} catch (e) {}
}

export const themePicker = {
	// IDEMPOTENT — buildModal handles existence check internally
	open() {
		buildModal();
	},
	close,
	applyStoredCustomHex,
};

// On bundle load, apply stored hex
if (typeof document !== "undefined") {
	if (document.readyState !== "loading") {
		applyStoredCustomHex();
	} else {
		document.addEventListener("DOMContentLoaded", applyStoredCustomHex, { once: true });
	}
}
